import { differenceInDays } from 'date-fns';
import { logger } from '@/lib';
import { TIME_CONSTANTS } from '@/lib';
import type { MembershipDateRange } from './constants';

/**
 * Normalize a Date to a stable point in the day so comparisons are
 * independent of the original time-of-day / timezone noise.
 */
export const normalizeDateForComparison = (
  date: Date,
  timeOfDay: 'start' | 'middle' | 'end' = 'middle'
): Date => {
  const normalized = new Date(date);

  switch (timeOfDay) {
    case 'start':
      normalized.setHours(0, 0, 0, 0);
      break;
    case 'end':
      normalized.setHours(
        TIME_CONSTANTS.END_OF_DAY_HOUR,
        TIME_CONSTANTS.END_OF_DAY_MINUTE,
        TIME_CONSTANTS.END_OF_DAY_SECOND,
        TIME_CONSTANTS.END_OF_DAY_MILLISECOND
      );
      break;
    case 'middle':
    default:
      normalized.setHours(
        TIME_CONSTANTS.MIDDLE_OF_DAY_HOUR,
        TIME_CONSTANTS.MIDDLE_OF_DAY_MINUTE,
        TIME_CONSTANTS.MIDDLE_OF_DAY_SECOND,
        TIME_CONSTANTS.MIDDLE_OF_DAY_MILLISECOND
      );
      break;
  }

  return normalized;
};

export const calculateMembershipDuration = (membership: MembershipDateRange): number | null => {
  const { start_date, end_date } = membership;

  if (!start_date || !end_date) return null;

  try {
    const startDate = new Date(start_date);
    const endDate = new Date(end_date);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

    return differenceInDays(endDate, startDate);
  } catch (error) {
    logger.error('Error calculating membership duration', error);
    return null;
  }
};

export const formatMembershipDateRange = (membership: MembershipDateRange): string => {
  const { start_date, end_date } = membership;

  if (!start_date && !end_date) return 'No dates available';

  if (!start_date) {
    try {
      const endDate = new Date(end_date!);
      if (isNaN(endDate.getTime())) return 'No dates available';
      return `No start date - ${endDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })}`;
    } catch (error) {
      logger.error('Error formatting end date', error);
      return 'No dates available';
    }
  }

  if (!end_date) {
    try {
      const startDate = new Date(start_date);
      if (isNaN(startDate.getTime())) return 'No dates available';
      return `${startDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })} - No end date`;
    } catch (error) {
      logger.error('Error formatting start date', error);
      return 'No dates available';
    }
  }

  try {
    const startDate = new Date(start_date);
    const endDate = new Date(end_date);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 'No dates available';

    return `${startDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })} - ${endDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })}`;
  } catch (error) {
    logger.error('Error formatting date range', error);
    return 'No dates available';
  }
};

export const formatCancelledDate = (cancelledAt: string | null | undefined): string => {
  if (!cancelledAt) return 'Cancelled';
  try {
    const date = new Date(cancelledAt);
    if (isNaN(date.getTime())) return 'Cancelled';
    return `Cancelled on ${date.toLocaleDateString('en-GB')}`;
  } catch {
    return 'Cancelled';
  }
};
